/**
 * AbnLookup
 * ─────────
 * Single, reusable ABN entry + verification widget.
 *
 *   1. User types/pastes ABN (any format — spaces, dashes, dots are stripped).
 *   2. On 11 digits, fires GET /api/wp/abr-lookup?abn=…
 *   3. Renders the legal entity name. If ABR returns multiple trading
 *      names (e.g. "Hustle Plaza", "Hustle Plate", "EZIBIZ" all on one
 *      ABN), shows a dropdown so the user can pick which brand this
 *      claim/registration is for.
 *   4. Calls onResolve({ abn, entity_name, business_name, status, raw })
 *      whenever the selection changes — caller can use those values in
 *      the rest of their form.
 *
 * Props:
 *   value         — controlled string ABN (formatted however)
 *   onChange(v)   — update the raw text
 *   onResolve(r)  — called with the verification payload + chosen name
 *   testId        — optional data-testid prefix (default "abn-lookup")
 *
 * Backend contract: see /app/backend/wp_clients.py:_asic_lookup
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2, XCircle, Building2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const stripDigits = (s) => String(s || "").replace(/\D/g, "");

export default function AbnLookup({
  value,
  onChange,
  onResolve,
  testId = "abn-lookup",
  label = "ABN",
  // When false, shows a "No ABN — private seller / individual" toggle
  // so Property private-sale flows (and similar) can record an explicit
  // not_required decision instead of leaving the field blank.
  required = true,
  notRequiredLabel = "I don't have an ABN (private seller / individual)",
}) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chosenName, setChosenName] = useState("");
  const [notRequired, setNotRequired] = useState(false);
  const lastQueried = useRef("");
  const cleaned = stripDigits(value);

  // When the user opts out ("not required"), tell the parent and clear
  // any prior verification state.
  useEffect(() => {
    if (notRequired) {
      onResolve?.({
        abn:           null,
        entity_name:   null,
        business_name: null,
        status:        "not_required",
        trading_names: [],
        modules:       {},
        address:       {},
        cached:        false,
        raw:           null,
      });
    }
    // We intentionally don't depend on onResolve — callers create a
    // fresh handler each render and the toggle should only fire on a
    // real toggle change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notRequired]);

  // When the user is mid-typing (under 11 digits), don't show any result
  // — derive these locally instead of holding stale state for them.
  const isComplete = cleaned.length === 11;
  const displayResult = isComplete ? result : null;
  const displayError = isComplete ? error : null;

  useEffect(() => {
    if (!isComplete) return;
    if (cleaned === lastQueried.current) return;
    lastQueried.current = cleaned;
    setLoading(true);
    setError(null);

    fetch(`${API}/api/abn/lookup?abn=${cleaned}`)
      .then((r) => r.json())
      .then((d) => {
        setResult(d);
        // Auto-pick: if only one trading name, lock it in. If the entity
        // has no trading names, fall back to the legal entity name.
        const defaultPick =
          (d.trading_names || []).length === 1
            ? d.trading_names[0]
            : (d.trading_names || []).length === 0
              ? (d.entity_name || "")
              : ""; // leave blank so user must choose
        setChosenName(defaultPick);
        onResolve?.({
          abn: d.abn,
          entity_name: d.entity_name,
          business_name: defaultPick,
          status: d.status,
          trading_names: d.trading_names || [],
          modules: d.modules || {},
          address: d.address || {},
          cached: d.cached,
          raw: d.raw_abr || d.raw,
        });
      })
      .catch((e) => setError(e.message || "Lookup failed"))
      .finally(() => setLoading(false));
  }, [cleaned, onResolve]);

  const handleNameChange = (name) => {
    setChosenName(name);
    if (result) {
      onResolve?.({
        abn: result.abn,
        entity_name: result.entity_name,
        business_name: name,
        status: result.status,
        trading_names: result.trading_names || [],
        modules: result.modules || {},
        address: result.address || {},
        cached: result.cached,
        raw: result.raw_abr || result.raw,
      });
    }
  };

  const verified = result?.status === "verified";
  const rejected = result?.status === "rejected";

  return (
    <div data-testid={testId} className="space-y-2">
      <label className="block text-[11px] uppercase tracking-wider font-bold text-stone-600">
        {label}
      </label>

      {/* "No ABN" opt-out — only rendered when the caller says ABN is
          optional (e.g. Property private sales, individual hosts). */}
      {!required && (
        <label
          className="flex items-center gap-2 text-[12px] text-stone-700 cursor-pointer select-none"
          data-testid={`${testId}-not-required-toggle`}
        >
          <input
            type="checkbox"
            checked={notRequired}
            onChange={(e) => setNotRequired(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-stone-300 text-stone-900"
          />
          {notRequiredLabel}
        </label>
      )}

      <div className={`relative ${notRequired ? "opacity-40 pointer-events-none" : ""}`}>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="e.g. 16 160 565 775"
          data-testid={`${testId}-input`}
          inputMode="numeric"
          autoComplete="off"
          disabled={notRequired}
          className="w-full pl-3 pr-10 py-2 rounded-md border border-stone-300 bg-white text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-stone-900/30 disabled:bg-stone-100"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-stone-400" data-testid={`${testId}-loading`} />}
          {!loading && verified && <CheckCircle2 className="w-4 h-4 text-emerald-600" data-testid={`${testId}-ok`} />}
          {!loading && rejected && <XCircle className="w-4 h-4 text-red-600" data-testid={`${testId}-bad`} />}
        </div>
      </div>

      {/* Helper text */}
      {cleaned && cleaned.length > 0 && cleaned.length < 11 && (
        <p className="text-[11px] text-stone-500" data-testid={`${testId}-hint`}>
          {cleaned.length} / 11 digits — keep typing.
        </p>
      )}

      {displayError && (
        <p className="text-[12px] text-red-600" data-testid={`${testId}-error`}>{displayError}</p>
      )}

      {/* Verified entity panel */}
      {verified && displayResult && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2"
          data-testid={`${testId}-panel`}
        >
          <div className="flex items-center gap-2 text-[13px] font-bold text-emerald-900">
            <Building2 className="w-4 h-4" />
            {displayResult.entity_name}
          </div>

          {/* Cross-module registration hint — shows when this ABN has
              already been registered on other modules. Saves the user
              re-entering data and signals network effects. */}
          {(() => {
            const mods = displayResult.modules || {};
            const tags = Object.entries(mods)
              .filter(([, arr]) => Array.isArray(arr) && arr.length > 0)
              .map(([k, arr]) => ({ key: k, n: arr.length }));
            if (tags.length === 0) return null;
            const labels = {
              directory: "Directory", plugin: "WP Plugin", plate: "Plate",
              plaza: "Plaza", property: "Property", deals: "Deals",
              chat: "Chat", music: "Music",
            };
            return (
              <div
                data-testid={`${testId}-existing-modules`}
                className="flex items-start gap-2 rounded-md bg-white/70 border border-emerald-200 px-2 py-1.5"
              >
                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 shrink-0 pt-0.5">
                  Already on
                </span>
                <div className="flex flex-wrap gap-1">
                  {tags.map((t) => (
                    <span
                      key={t.key}
                      data-testid={`${testId}-existing-${t.key}`}
                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300"
                    >
                      {labels[t.key] || t.key}
                      {t.n > 1 ? ` ×${t.n}` : ""}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          {(displayResult.trading_names || []).length > 1 ? (
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-bold text-emerald-800 mb-1">
                Choose the business this is for
              </label>
              <select
                value={chosenName}
                onChange={(e) => handleNameChange(e.target.value)}
                data-testid={`${testId}-name-select`}
                className="w-full px-2 py-1.5 rounded-md border border-emerald-300 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                <option value="">— Select a trading name —</option>
                <option value={displayResult.entity_name}>{displayResult.entity_name} (legal entity)</option>
                {(displayResult.trading_names || []).map((n, i) => (
                  <option key={i} value={n} data-testid={`${testId}-name-option-${i}`}>{n}</option>
                ))}
              </select>
            </div>
          ) : (displayResult.trading_names || []).length === 1 ? (
            <p className="text-[12px] text-emerald-800">
              Trading as <strong>{displayResult.trading_names[0]}</strong>
            </p>
          ) : null}
        </div>
      )}

      {rejected && displayResult && (
        <p className="text-[12px] text-red-700" data-testid={`${testId}-rejected`}>
          {displayResult.raw?.reason
            ? displayResult.raw.reason
            : displayResult.raw?.Message
              ? displayResult.raw.Message
              : "ABN could not be verified."}
        </p>
      )}
    </div>
  );
}
